import type { ComponentProps, FC } from "react"
import { styled } from "styled-system/jsx"

const BaseSwitch = styled("input", {
  base: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    width: "[42px]",
    height: "xs",
    padding: "[2px]",
    borderRadius: "full",
    backgroundColor: "secondary",
    cursor: "pointer",
    transitionProperty: "[background-color]",
    transitionDuration: "[200ms]",
    _before: {
      content: '""',
      display: "block",
      width: "[18px]",
      height: "[18px]",
      borderRadius: "full",
      backgroundColor: "text.onDark",
      boxShadow: "[0 1px 2px rgba(15, 23, 42, 0.35)]",
      transitionProperty: "[transform, box-shadow]",
      transitionDuration: "[200ms]",
    },
    _checked: {
      backgroundColor: "primary",
      _before: {
        transform: "translateX(18px)",
        boxShadow: "[0 2px 4px rgba(15, 23, 42, 0.35)]",
      },
    },
    _disabled: {
      opacity: "muted",
      cursor: "not-allowed",
    },
    _focusVisible: {
      outline: "[2px solid]",
      outlineColor: "primary",
      outlineOffset: "[2px]",
    },
  },
})

export type SwitchProps = Omit<
  ComponentProps<typeof BaseSwitch>,
  "aria-checked" | "checked" | "defaultChecked" | "onChange" | "role" | "type"
> & {
  checked: boolean
  onCheckedChange(checked: boolean): void
}

export const Switch: FC<SwitchProps> = (props) => {
  const { checked, onCheckedChange, disabled, ...rest } = props

  return (
    <BaseSwitch
      {...rest}
      type="checkbox"
      role="switch"
      checked={checked}
      // Use `aria-disabled` rather than the native `disabled` attribute: a
      // native-disabled input is inert to pointer events, which would stop a
      // wrapping tooltip from ever firing on hover. The change is guarded below.
      aria-disabled={disabled ? true : undefined}
      onChange={(event) => {
        if (disabled) {
          return
        }
        onCheckedChange(event.target.checked)
      }}
    />
  )
}
